import { HttpClient } from '@angular/common/http';
import { ErrorHandler, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { LocalStorageService } from '@app/core/service/local-storage.service';
import { ToastService } from '@app/core/service/toast.service';
import { UserEntityService } from '@app/core/service/user-entity.service';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import {
  ActionPerformed,
  PushNotifications,
  PushNotificationSchema,
  Token
} from '@capacitor/push-notifications';
import { EMPTY, from, Observable, ReplaySubject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  switchMap,
  tap
} from 'rxjs/operators';

/**
 * Provides a base for authentication workflow.
 * The User interface as well as login/logout methods should be replaced with proper implementation.
 */
@Injectable({ providedIn: 'root' })
export class FcmService {
  private _currentUserId: string | null = null;
  private _currentUserFcmToken: string | null = null;
  private _currentDeviceToken: string | null = null;

  public lastPushNotification$ = new ReplaySubject<{
    title: string;
    message: string;
    target: string;
  } | null>(1);

  public constructor(
    private readonly _httpClient: HttpClient,
    private readonly _localStorageService: LocalStorageService,
    private readonly _toastService: ToastService,
    private readonly _router: Router,
    private readonly _ngZone: NgZone,
    private readonly _errorHandler: ErrorHandler,
    private readonly _userEntityService: UserEntityService
  ) {}

  /**
   * Remove user notification token
   */
  private _removeNotificationToken(userId: string) {
    return this._httpClient.post(`/user/notification`, {
      userId,
      fcmToken: ''
    });
  }

  private _removeNotificationTokenSafely(userId: string) {
    this._removeNotificationToken(userId)
      .pipe(
        tap(() => {
          this._currentUserFcmToken = null;
        }),
        catchError((error) => {
          this._errorHandler.handleError(error);
          return EMPTY;
        })
      )
      .subscribe();
  }

  private _bindNotificationTokenSafely(userId: string, fcmToken: string) {
    this._httpClient
      .post(`/user/notification`, {
        userId,
        fcmToken
      })
      .subscribe({
        next: () => {
          this._currentUserFcmToken = fcmToken;
        },
        error: (error) => this._errorHandler.handleError(error)
      });
  }

  /**
   * Refresh user notification token
   */
  public refreshNotificationToken() {
    if (!this._currentUserId) {
      return;
    }

    if (this._isMultiUserMode()) {
      if (this._currentUserFcmToken) {
        this._removeNotificationTokenSafely(`${this._currentUserId}`);
      }
      return;
    }

    if (
      Capacitor.getPlatform() !== 'web' &&
      this._currentDeviceToken &&
      this._currentUserId &&
      this._currentDeviceToken !== this._currentUserFcmToken
    ) {
      this._bindNotificationTokenSafely(
        this._currentUserId,
        this._currentDeviceToken
      );
    }
  }

  private _isMultiUserMode(): boolean {
    return !!this._localStorageService.getItem('multiUserMode');
  }

  public initFCM(): Observable<void> {
    if (Capacitor.getPlatform() === 'web') {
      return EMPTY;
    }

    return new Observable<void>(() => {
      const listenerHandles: Array<Promise<PluginListenerHandle>> = [];

      listenerHandles.push(
        PushNotifications.addListener('registration', (token: Token) => {
          this._currentDeviceToken = token.value;
          this.refreshNotificationToken();
        })
      );

      listenerHandles.push(
        PushNotifications.addListener(
          'pushNotificationReceived',
          (notification: PushNotificationSchema) => {
            const notificationData = notification?.data as
              | {
                  title?: string;
                  message?: string;
                  target?: string;
                  hideToast?: boolean;
                }
              | undefined;

            if (this._isMultiUserMode() || notificationData?.hideToast) {
              return;
            }

            this._toastService.presentToast(
              `${notificationData?.title || ''}\n${
                notificationData?.message || ''
              }`,
              {
                okText: 'Open',
                okCallback: () => {
                  const target = notificationData?.target;
                  if (!target) {
                    return;
                  }

                  this._ngZone.run(() => {
                    this.lastPushNotification$.next(notificationData as any);
                    this._router.navigateByUrl(target);
                  });
                }
              }
            );
          }
        )
      );

      listenerHandles.push(
        PushNotifications.addListener(
          'pushNotificationActionPerformed',
          (notification: ActionPerformed) => {
            if (this._isMultiUserMode()) {
              return;
            }

            this._ngZone.run(() => {
              const notificationData = notification?.notification?.data as
                | { target?: string; title?: string; message?: string }
                | undefined;
              const targetUrl = notificationData?.target;
              if (!targetUrl) {
                return;
              }

              let routePath = targetUrl;
              if (targetUrl.includes('#/')) {
                routePath = targetUrl.split('#')[1];
              }

              this.lastPushNotification$.next(notificationData as any);
              this._router.navigateByUrl(routePath);
            });
          }
        )
      );

      const userSubscription = this._userEntityService.user$
        .pipe(
          distinctUntilChanged((prev, curr) => prev?._id === curr?._id),
          switchMap((user) => {
            const previousUserId = this._currentUserId;
            const previousUserFcmToken = this._currentUserFcmToken;

            if (!user) {
              this._currentUserId = null;
              this._currentUserFcmToken = null;

              if (previousUserId && previousUserFcmToken) {
                this._removeNotificationTokenSafely(previousUserId);
              }

              return EMPTY;
            }

            this._currentUserId = user._id;
            this._currentUserFcmToken = user.fcmToken || null;

            return from(PushNotifications.requestPermissions()).pipe(
              tap((result) => {
                if (result.receive === 'granted') {
                  if (this._isMultiUserMode()) {
                    return;
                  }

                  PushNotifications.register();
                  this.refreshNotificationToken();
                }
              })
            );
          }),
          catchError((error) => {
            this._errorHandler.handleError(error);
            return EMPTY;
          })
        )
        .subscribe();

      return () => {
        userSubscription.unsubscribe();
        listenerHandles.forEach((handlePromise) => {
          handlePromise
            .then((handle) => handle.remove())
            .catch((error) => this._errorHandler.handleError(error));
        });
      };
    });
  }
}
